import React, { ReactNode, useLayoutEffect, useRef, useState } from 'react'
import ResizeObserver from 'resize-observer-polyfill'
import styled from 'styled-components/macro'
import {
  Tooltip,
  TooltipOverlayType,
  TooltipPositions,
} from '../Tooltip/Tooltip'
import { BasicChip } from './BasicChip'
import { OverflowCountChip } from './ChipSummary'

const CHIP_GAP = 4
// Asking for more than this would crowd the other columns, so a long list collapses
// into "+ N" unless the grid has room to spare and stretches the column.
const MAX_NATURAL_WIDTH = 320

// A grid cell holding a FittedChipList needs this min-width (it includes the cell's 16px
// side padding) to fit a truncated first chip and "+ N". An explicit min-width also
// stops the chips' full width from becoming the column's minimum.
export const FITTED_CHIP_LIST_CELL_MIN_WIDTH = '172px'

const Container = styled.div`
  flex: 1 1 auto;
  min-width: 0;
`

// Lays out every chip off screen: its width is what the column asks for, and its
// chips are measured to decide how many fit.
const Sizer = styled.div`
  display: flex;
  gap: ${CHIP_GAP}px;
  max-width: ${MAX_NATURAL_WIDTH}px;
  height: 0;
  overflow: hidden;
  visibility: hidden;
`

// Sized by its container only, so the chips it shows can't feed back into the
// column width they were chosen for.
const VisibleRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${CHIP_GAP}px;
  overflow: hidden;
  contain: inline-size;
`

const MeasuredMoreChip = styled.div`
  display: flex;
  position: absolute;
  visibility: hidden;
`

const width = (el: Element) => el.getBoundingClientRect().width

// Widths are fractional and the column is often sized to exactly the chips' width, so
// allow for rounding before deciding something doesn't fit.
const fits = (needed: number, available: number) => needed <= available + 0.5

// The most chips, in order, that fit with "+ N" after them; always at least the
// first, which truncates when even it doesn't fit.
const countFitting = (
  chipWidths: number[],
  moreWidth: number,
  available: number,
): number => {
  let used = 0
  for (let i = 0; i < chipWidths.length; i++) {
    used += (i > 0 ? CHIP_GAP : 0) + chipWidths[i]
    const hasMore = i < chipWidths.length - 1
    if (!fits(used + (hasMore ? CHIP_GAP + moreWidth : 0), available)) {
      return Math.max(i, 1)
    }
  }
  return chipWidths.length
}

/**
 * As many of items as fit the cell, each as a chip, then a "+ N" chip for the rest.
 * The tooltip shows when something is hidden or cut short, or always if asked.
 */
export const FittedChipList = ({
  items,
  tooltipId,
  tooltipBody,
  alwaysShowTooltip = false,
}: {
  items: string[]
  tooltipId: string
  tooltipBody: ReactNode
  alwaysShowTooltip?: boolean
}): JSX.Element => {
  const itemsKey = items.join('\n')
  const rowRef = useRef<HTMLDivElement>(null)
  const sizerRef = useRef<HTMLDivElement>(null)
  const moreRef = useRef<HTMLDivElement>(null)
  const [available, setAvailable] = useState(0)
  const [chipWidths, setChipWidths] = useState<number[]>([])
  const [moreWidth, setMoreWidth] = useState(0)

  useLayoutEffect(() => {
    const row = rowRef.current
    const sizer = sizerRef.current
    if (!row || !sizer) return
    setChipWidths(Array.from(sizer.children, width))
    setMoreWidth(moreRef.current ? width(moreRef.current) : 0)
    setAvailable(width(row))
    const observer = new ResizeObserver(([entry]) =>
      setAvailable(entry.contentRect.width),
    )
    observer.observe(row)
    return () => observer.disconnect()
  }, [itemsKey])

  const visibleCount = countFitting(chipWidths, moreWidth, available)
  const hiddenCount = items.length - visibleCount
  const isFirstTruncated =
    visibleCount === 1 &&
    !fits(
      (chipWidths[0] ?? 0) + (hiddenCount > 0 ? CHIP_GAP + moreWidth : 0),
      available,
    )

  return (
    <Tooltip
      id={tooltipId}
      placement={TooltipPositions.top}
      overlayType={TooltipOverlayType.popover}
      tooltipBody={tooltipBody}
      disabled={!alwaysShowTooltip && hiddenCount === 0 && !isFirstTruncated}
    >
      <Container>
        <Sizer ref={sizerRef} aria-hidden>
          {items.map((item, i) => (
            <BasicChip key={i} flex="0 0 auto" maxWidth="none">
              {item}
            </BasicChip>
          ))}
        </Sizer>
        {items.length > 1 && (
          <MeasuredMoreChip ref={moreRef} aria-hidden>
            <OverflowCountChip count={items.length - 1} />
          </MeasuredMoreChip>
        )}
        <VisibleRow ref={rowRef}>
          {items.slice(0, visibleCount).map((item, i) => (
            <BasicChip
              key={i}
              flex={i === 0 ? '0 1 auto' : '0 0 auto'}
              maxWidth="none"
            >
              {item}
            </BasicChip>
          ))}
          {hiddenCount > 0 && <OverflowCountChip count={hiddenCount} />}
        </VisibleRow>
      </Container>
    </Tooltip>
  )
}
